import React from 'react'
import { Todo } from '../App'
import {
  DndContext, 
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  defaultDropAnimation
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { SortableTodoItem } from './SortableTodoItem'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'

interface TodoListProps {
  todos: Todo[]
  filter: 'all' | 'active' | 'completed'
  onToggle: (id: string) => void
  onUpdate: (id: string, text: string) => void
  onDelete: (id: string) => void
  onReorder: (sourceIndex: number, destinationIndex: number) => void
}

const TodoList: React.FC<TodoListProps> = ({ 
  todos, 
  filter, 
  onToggle, 
  onUpdate, 
  onDelete,
  onReorder
}) => {
  // Configure sensors with minimal constraints
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = todos.findIndex(todo => todo.id === active.id);
      const newIndex = todos.findIndex(todo => todo.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(oldIndex, newIndex);
      }
    }
  };

  if (todos.length === 0) {
    return (
      <ul id="todo-list">
        <li className="todo-item" style={{ justifyContent: 'center' }}>
          {filter === 'all' 
            ? 'No todos yet. Add one above!' 
            : `No ${filter} todos`}
        </li>
      </ul>
    )
  }

  return (
    <DndContext 
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext 
        items={todos.map(todo => todo.id)} 
        strategy={verticalListSortingStrategy}
      >
        <ul id="todo-list">
          {todos.map((todo) => (
            <SortableTodoItem
              key={todo.id}
              todo={todo}
              onToggle={onToggle}
              onUpdate={onUpdate}
              onDelete={onDelete}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  )
}

export default TodoList 